import ContainerFlex from "components/ContainerFlex";
import Input from "components/input";
import KeyboardWrapper from "components/Keyboard";
import useApi, { ApiMethod, IResponseUseApi } from "hooks/useApi";
import { BuscarClienteCpfResponse } from "models/clienteCpfResponse";
import { SecurityPageProps } from "models/security";
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/app/hooks";
import { addCPFUnlockCartao } from "redux/features/cartao/desbloqueio-slice";
import { setCliente } from "redux/features/cliente/cliente-slice";
import { addTypeFlowState } from "redux/features/security/security-slice";
import { resetLoading, setLoading } from "redux/ui/loadings-slice";
import { RouteNames } from "routes/routes";
import Endpoints from "services/api/endpoints";
import Swal from "sweetalert2";
import { ClientStatus, isBlockedCard } from "utils/clientStatus";
import { cpfMask, cpfUnmask } from "utils/mask";
import { showMessage } from "utils/messages";
import { erroJaTratado } from "utils/systemError";
import { saveItemLocalStore } from "utils/storeUtils";
import { LOCAL_STORE_CLIENTE } from "../../../constants";
import "./style.css";

type IResponseClientCpf = IResponseUseApi<BuscarClienteCpfResponse>;

function getStatusesCartoes(dadosCliente: any): string[] {
  const temCartaoTeucard = !!dadosCliente?.cartaoAtual;
  const temCartaoConvenio = !!dadosCliente?.cartaoAtualConvenio;
  const statuses = [];
  if (temCartaoTeucard) {
    statuses.push(dadosCliente?.cartaoAtual?.status);
  }
  if (temCartaoConvenio) {
    statuses.push(dadosCliente?.cartaoAtualConvenio?.status);
  }
  return statuses;
}

function saoTodosCartoesInativos(dadosCliente: any): boolean {
  return getStatusesCartoes(dadosCliente).every((s) => s === "INATIVO");
}

const ClienteCPF: React.FC<SecurityPageProps> = ({
  title,
  subDescription,
  description = "",
  nextStep,
  backStep,
  fluxoUnlock = false,
}) => {
  const [input, setInput] = useState("");
  const [value, setValue] = useState("");

  const keyboard = useRef(null);

  const history = useHistory();

  function validaStatusCartoes(dadosCliente: any) {
    const statuses = getStatusesCartoes(dadosCliente);

    const todosCartoesCancelados = statuses.every((s) =>
      ["CANCELAMENTO_EXPERIENCIA", "CANCELAMENTO_SOLICITACAO"].includes(s),
    );

    if (todosCartoesCancelados) {
      dispatch(setLoading(false));
      showMessage(
        "warning",
        "Atenção!",
        "Cartão Cancelado! Favor realizar nova proposta.",
        {
          timer: 5000,
          showConfirmButton: true,
          didClose() {
            history.replace(RouteNames.HOME);
          },
        },
      );
      return false;
    }
    const todosCartoesBloqueados = statuses.every((s) =>
      ["PERDA_EXTRAVIO", "FURTO_ROUBO", "ERRO_IMPRESSAO", "CANCELADO"].includes(
        s,
      ),
    );
    if (todosCartoesBloqueados) {
      dispatch(setLoading(false));
      showMessage(
        "warning",
        "Atenção!",
        "Cartão Bloqueado! Favor solicitar outra via no atendimento.",
        {
          timer: 5000,
          showConfirmButton: true,
          didClose() {
            history.replace(RouteNames.HOME);
          },
        },
      );
      return false;
    }
    return true;
  }
  const dispatch = useAppDispatch();
  const {
    cliente,
    cliente: { nome, cartaoAtual },
    cartaoBloqueado,
  } = useAppSelector((state) => state.cliente);

  const { typeFlow } = useAppSelector((state) => state.security);

  useEffect(() => {
    setValue(cpfMask(input));
  }, [input]);

  const validaUsuarioApenasConvenio = (
    dadosCliente: BuscarClienteCpfResponse,
  ) => {
    const temTeucardCancelado = isBlockedCard(
      dadosCliente?.cartaoAtual?.status,
    );
    if (
      (temTeucardCancelado || !dadosCliente?.cartaoAtual) &&
      !!dadosCliente?.cartaoAtualConvenio
    ) {
      showMessage(
        "warning",
        "Ops...",
        "Funcionalidade apenas para clientes TeuCard!",
        {
          timer: 5000,
          showConfirmButton: true,
          didClose() {
            history.replace(RouteNames.HOME);
          },
        },
      );
      return false;
    }
    return true;
  };
  const getClientByCpf = useApi<BuscarClienteCpfResponse>(
    `${Endpoints.usuario.searchUsuarioByCPFTotem}/${cpfUnmask(value)}`,
    ApiMethod.GET,
    {},
    {},
    { usePublicToken: true },
  );
  const handleSubmit = async () => {
    try {
      dispatch(setLoading(true));

      dispatch(setCliente({ ...cliente, cpf: cpfUnmask(value) }));
      typeFlow === "desbloqueio" &&
        dispatch(addCPFUnlockCartao(cpfUnmask(value)));

      const response = await getClientByCpf.callApi();
      if (response.status < 200 || response.status >= 300) {
        // 401/403 (fora do horario funcional) ja mostra a propria mensagem e permanece
        // na tela (ver hooks/useApi.ts e services/api/index.ts) — aqui so encerra o
        // fluxo, sem duplicar o modal.
        if (response.horarioRestritoTratado) {
          return;
        }
        throw new Error(response?.mensagem);
      }
      const dadosCliente = response.data;
      if (!dadosCliente) {
        throw new Error(response?.mensagem || "Resposta vazia do servidor.");
      }
      if (!validaStatusCartoes(dadosCliente)) {
        return;
      }

      // flows que sao para ambos clientes
      if (typeFlow === "limite") {
        handleLimite(response);
        return;
      }
      if (typeFlow === "atendimento") {
        handleAtendimento(response);
        return;
      }
      if (typeFlow === "desbloqueio") {
        await handleUnblock();
        return;
      }

      if (!validaUsuarioApenasConvenio(dadosCliente)) {
        return;
      }
      // Fatura e proposta continuam disponíveis apenas para cliente TeuCard.
      switch (typeFlow) {
        case "fatura":
          handleFatura(response);
          break;
        case "proposta":
          handleProposta();
          break;
      }
    } catch (error) {
      // Se veio do handshake de token-publico (axios) bloqueado por 401/403 (fora do
      // horario funcional), a mensagem ja foi mostrada em services/api/index.ts — nao
      // duplica aqui.
      if (!erroJaTratado(error)) {
        showMessage(
          "error",
          "Erro",
          error instanceof Error
            ? error.message
            : "Não foi possível consultar o CPF.",
        );
      }
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const {
    cartao: { validarCartao },
  } = useAppSelector((state) => state.cartaoDesbloqueio);

  async function handleLimite(response: IResponseClientCpf) {
    try {
      if (response.status >= 200 && response.status < 300) {
        const dadosCliente = response.data;

        saveItemLocalStore(LOCAL_STORE_CLIENTE, {
          ...dadosCliente,
          cpf: cpfUnmask(value),
        });

        const propsRouter: SecurityPageProps = {
          title: "Teu Limite",
          description: `<span>Por favor, digite <span>tua SENHA</span> e pressione a tecla verde (ENTRA),<br /> para continuar a operação:</span>`,
          nextStep: RouteNames.ATENDIMENTO_LIMITE,
          backStep: RouteNames.HOME,
        };

        if (dadosCliente?.statusCliente === ClientStatus.COBRANCA) {
          history.push(RouteNames.NEGOCIE);
        } else {
          console.log({ propsRouter, cliente });
          history.push({
            pathname: RouteNames.SECURITY_CLIENTE_SENHA,
            state: { ...propsRouter, cliente },
          });
        }

        dispatch(setCliente({ ...dadosCliente, cpf: cpfUnmask(value) }));
      } else {
        showMessage("warning", "Atenção!", String(response.mensagem), {
          timer: 5000,
          showConfirmButton: true,
        });
      }

      if (response.status > 400) {
        showMessage("warning", "Atenção!", String(response.mensagem), {
          timer: 5000,
          showConfirmButton: true,
        });
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function handleFatura(response: IResponseClientCpf) {
    try {
      if (response.status >= 200 && response.status < 300) {
        const dadosCliente = response.data;

        saveItemLocalStore(LOCAL_STORE_CLIENTE, {
          ...dadosCliente,
          cpf: cpfUnmask(value),
        });

        const propsRouter: SecurityPageProps = {
          title: "Tua Fatura",
          description: `<span>Por favor, digite <span>tua SENHA</span> e pressione a tecla verde (ENTRA),<br /> para continuar a operação:</span>`,
          nextStep: RouteNames.ATENDIMENTO_FATURA,
          backStep: RouteNames.HOME,
        };

        if (dadosCliente?.statusCliente === ClientStatus.COBRANCA) {
          history.push(RouteNames.NEGOCIE);
        } else {
          console.log({ propsRouter, cliente });
          history.push({
            pathname: RouteNames.SECURITY_CLIENTE_SENHA,
            state: { ...propsRouter, cliente },
          });
        }

        dispatch(setCliente({ ...dadosCliente, cpf: cpfUnmask(value) }));
      } else {
        showMessage("warning", "Atenção!", String(response.mensagem), {
          timer: 5000,
          showConfirmButton: true,
        });
      }

      if (response.status > 400) {
        showMessage("warning", "Atenção!", String(response.mensagem), {
          timer: 5000,
          showConfirmButton: true,
        });
      }
    } catch (err) {}
  }

  async function handleAtendimento(response: IResponseClientCpf) {
    try {
      if (response.status >= 200 && response.status < 300) {
        const dadosCliente = response.data;
        if (!dadosCliente) {
          return;
        }
        const todosCartoesInativos = saoTodosCartoesInativos(dadosCliente);
        if (todosCartoesInativos) {
          Swal.fire({
            icon: "question",
            text: "Identificamos que o teu cartão está inativo, você precisa desbloquear",
            showCancelButton: false,
            confirmButtonText: "Entendi",
            confirmButtonColor: "#33A733",
          }).then((result) => {
            if (result.isConfirmed) {
              history.push({
                pathname: RouteNames.HOME,
              });
            } else {
              history.push({
                pathname: RouteNames.HOME,
              });
            }
          });
        }

        if (dadosCliente.nome) {
          if (!todosCartoesInativos) {
            // Salva dados do cliente na storge e no redux

            saveItemLocalStore(LOCAL_STORE_CLIENTE, {
              ...dadosCliente,
              cpf: cpfUnmask(value),
            });

            showMessage(
              "success",
              `Seja bem-vindo(a) ${dadosCliente.nome}`,
              "",
              {
                timer: 1500,
                showConfirmButton: false,
              },
            );
            dispatch(setCliente({ ...dadosCliente, cpf: cpfUnmask(value) }));
          } else {
            dispatch(resetLoading());
            return dadosCliente;
          }
        }

        const propsRouter: SecurityPageProps = {
          nextStep: RouteNames.ATENDIMENTO_HOME,
          backStep: RouteNames.HOME,
        };

        history.push({
          pathname: RouteNames.ATENDIMENTO_HOME,
          state: { ...propsRouter },
        });

        dispatch(setLoading(false));
      }

      if (response.status > 400) {
        showMessage("warning", "Atenção!", String(response.mensagem), {
          timer: 5000,
          showConfirmButton: true,
        });
        dispatch(setLoading(false));
      }
    } catch (err) {}
  }

  async function handleUnblock() {
    if (cpfUnmask(value) !== validarCartao?.cpf) {
      showMessage(
        "warning",
        "Desbloqueio de Cartão!",
        "CPF informado diferente do cadastrado para este cartão. Verifique!",
        { timer: 3500, showConfirmButton: false },
      );
      return;
    }

    // A acao vem do validar-cartao, que conhece o tipo do cartao. TeuCard sempre cria
    // senha; so o Convenio de quem ja tem senha propria cai no INFORMAR.
    const nextStepAposDataNascimento =
      validarCartao?.acaoSenha === "INFORMAR"
        ? RouteNames.SECURITY_SENHA_DESBLOQUEIO_USUARIO_ATIVO
        : RouteNames.UNLOCK_CLIENTE_CRIAR_SENHA;

    const propsRouter: SecurityPageProps = {
      nextStep: nextStepAposDataNascimento,
      backStep: RouteNames.HOME,
      fluxoUnlock: true,
    };

    history.push({
      pathname: RouteNames.UNLOCK_CLIENTE_DATA_NASCIMENTO,
      state: { ...propsRouter },
    });
  }

  function handleProposta() {
    const propsRouter: SecurityPageProps = {
      nextStep: RouteNames.TERMO_E_CONDICOES,
      backStep: RouteNames.HOME,
    };

    history.push({
      pathname: RouteNames.CLIENTE_CELULAR,
      state: { ...propsRouter },
    });
  }

  useEffect(() => {
    if (fluxoUnlock === false) {
      if (nome) {
        if (saoTodosCartoesInativos(cliente)) {
          Swal.fire({
            icon: "warning",
            title: "Que pena, seu cartão está Inativo!",
            text: "Deseja realizar o desbloqueio agora pelo auto atendimento?",
            showConfirmButton: true,
            confirmButtonText: "Sim",
            confirmButtonColor: "#33A733",
            showCancelButton: true,
            cancelButtonText: "Não",
            cancelButtonColor: "#D33E3E",
            timer: 10000,
          }).then((result) => {
            if (result.value) {
              dispatch(addTypeFlowState("desbloqueio"));
              const propsRouter: SecurityPageProps = {
                nextStep: RouteNames.SECURITY_CLIENTE_CPF,
                backStep: RouteNames.HOME,
                fluxoUnlock: true,
              };

              history.push({
                pathname: RouteNames.TERMO_E_CONDICOES,
                state: { ...propsRouter },
              });
            } else {
              history.push(backStep);
            }
          });
        } else if (cartaoAtual?.status !== ClientStatus.CANCELADO) {
          history.push(nextStep);
        } else {
          showMessage(
            "info",
            "Atenção!",
            "Seu cartão está bloqueado, procure um operador de vendas ou o SAC fone 4003-3203",
            { timer: 4000, showConfirmButton: false },
          );
          history.push(backStep);
        }
      }
    }
    // "cliente" (objeto Redux inteiro) fica de fora de proposito: ele muda a
    // cada dispatch(setCliente(...)) do fluxo de CPF, o que reabriria o
    // Swal/navegacao deste efeito em momentos nao relacionados ao status do cartao.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nome,
    cartaoBloqueado,
    cartaoAtual?.status,
    backStep,
    nextStep,
    fluxoUnlock,
    dispatch,
    history,
  ]);

  const handleVoltar = () => {
    history.push(backStep || RouteNames.HOME);
  };

  return (
    <div className="container">
      <h1>{title}</h1>

      <div
        className="container-description"
        dangerouslySetInnerHTML={{ __html: description }}
      />

      <ContainerFlex width={60}>
        <ContainerFlex marginBottom={10}>
          <Input
            label="Valor"
            style={{
              height: "10%",
              textAlign: "center",
              fontSize: "2rem",
              fontWeight: "bold",
            }}
            name="valorInfo"
            value={value}
            onChange={(e) => setValue(e)}
            edit
            disabled
          />
        </ContainerFlex>

        <KeyboardWrapper
          onPressBack={handleVoltar}
          keyboardRef={keyboard}
          onChange={setInput}
          typeKeyboard="numeric"
          onPressEnter={() => handleSubmit()}
        />
      </ContainerFlex>
    </div>
  );
};

export default ClienteCPF;
